import { useState } from "react";
import { Info } from "lucide-react";
import { ApiError } from "../../api/client.js";
import { useAddGrant, useNodes } from "../../api/hooks.js";
import type {
  CreateGrantBody,
  DriverType,
  GrantKind,
  GrantScope,
} from "../../api/types.js";
import { Button, Label, Select } from "../../components/ui.js";

const DRIVERS: DriverType[] = ["mediamtx", "srs", "cloudflare"];

// Rendered inside a Radix PopoverContent (which supplies the surface), so this
// returns just the form body.
export function AddGrantPopover({
  userId,
  onClose,
}: {
  userId: string;
  onClose: () => void;
}) {
  const nodes = useNodes();
  const addGrant = useAddGrant(userId);

  const [kind, setKind] = useState<GrantKind>("publish");
  const [scope, setScope] = useState<GrantScope>("node");
  const [nodeId, setNodeId] = useState<string>("");
  const [driver, setDriver] = useState<DriverType>("mediamtx");
  const [inlineError, setInlineError] = useState<string | null>(null);

  const nodeList = nodes.data ?? [];
  const effectiveNodeId = nodeId || nodeList[0]?.id || "";

  async function submit() {
    setInlineError(null);
    const body: CreateGrantBody = { kind, scope };
    if (scope === "node") {
      if (!effectiveNodeId) {
        setInlineError("Select a node");
        return;
      }
      body.nodeId = effectiveNodeId;
    } else if (scope === "driver") {
      body.driver = driver;
    }
    try {
      await addGrant.mutateAsync(body);
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.code === "GRANT_EXISTS") {
        setInlineError("This grant already exists");
        return;
      }
      // Other errors are toasted by the global mutation cache.
    }
  }

  const selectedNode = nodeList.find((n) => n.id === effectiveNodeId);

  return (
    <div>
      <h4 className="mb-3 font-display text-sm font-semibold text-foreground">
        Grant permission
      </h4>

      <fieldset className="mb-3">
        <Label>Kind</Label>
        <div className="flex gap-3 text-sm">
          {(["publish", "record"] as GrantKind[]).map((k) => (
            <label key={k} className="flex items-center gap-1.5 text-foreground/90">
              <input
                type="radio"
                name="kind"
                checked={kind === k}
                onChange={() => setKind(k)}
                className="accent-primary"
              />
              {k === "publish" ? "Publish" : "Record"}
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className="mb-3">
        <Label>Scope</Label>
        <div className="flex flex-col gap-1.5 text-sm">
          {(
            [
              ["node", "Specific node"],
              ["driver", "Whole driver"],
              ["global", "Global (all)"],
            ] as [GrantScope, string][]
          ).map(([s, label]) => (
            <label key={s} className="flex items-center gap-1.5 text-foreground/90">
              <input
                type="radio"
                name="scope"
                checked={scope === s}
                onChange={() => setScope(s)}
                className="accent-primary"
              />
              {label}
            </label>
          ))}
        </div>
      </fieldset>

      {scope === "node" && (
        <div className="mb-3">
          <Label>Node</Label>
          <Select
            value={effectiveNodeId}
            onChange={(e) => setNodeId(e.target.value)}
          >
            {nodeList.length === 0 && <option value="">No nodes</option>}
            {nodeList.map((n) => (
              <option key={n.id} value={n.id}>
                {n.slug} · {n.driver}
              </option>
            ))}
          </Select>
          {selectedNode && (
            <p className="mt-1 text-xs text-muted-foreground">
              driver: {selectedNode.driver}
            </p>
          )}
        </div>
      )}

      {scope === "driver" && (
        <div className="mb-3">
          <Label>Driver</Label>
          <Select
            value={driver}
            onChange={(e) => setDriver(e.target.value as DriverType)}
          >
            {DRIVERS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </Select>
        </div>
      )}

      {scope === "global" && (
        <div className="mb-3 flex items-start gap-1.5 rounded-lg border border-amber-500/30 bg-amber-500/10 p-2 text-xs text-amber-200">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Global grants apply on ANY node and driver.
        </div>
      )}

      {inlineError && (
        <p className="mb-2 text-xs text-destructive">{inlineError}</p>
      )}

      <div className="mt-3 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={submit}
          loading={addGrant.isPending}
        >
          Grant
        </Button>
      </div>
    </div>
  );
}
